import clsx from "clsx";
import { Swords, Users } from "lucide-react";

import type { OdbSide, OdbSummary } from "../../api/types";
import { Spinner } from "../../components/EmptyState";
import { Badge, SectionLabel, Segmented } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { k, lang, locale, t, tc } from "../../i18n/i18n";
import { plural } from "../../plural";
import { itemPrivate } from "../../sharing/access";
import { bi } from "./app6";
import { nationName } from "./nations";
import { UnitSymbol } from "./UnitSymbol";
import { Waiting } from "../../components/Waiting";

/** Liste des ordres de bataille d'un camp : bascule Amis / Ennemis en tête, ceux de l'utilisateur puis ceux fournis
 *  avec Generex (armée de Terre française ; ennemis des unités françaises, cachés quand l'éditeur est ouvert depuis
 *  un dossier qui affronte l'ennemi générique, registre B39). */

export const SIDES: { value: OdbSide; label: string; icon: typeof Users }[] = [
  { value: "AMI", label: k("Amis"), icon: Users },
  { value: "ENI", label: k("Ennemis"), icon: Swords },
];

/** Nature affichée : « GTIA » (« Battlegroup ») pour un bataillon en groupements tactiques, « Bataillon » pour un
 *  bataillon organique (une unité britannique n'est pas un « GTIA »). */
export function natureLabel(kind: string, label: string, organisation: string | null | undefined): string {
  return kind === "gtia" && organisation === "organique" ? tc("nature", "Bataillon") : tc("nature", label);
}

/** « 1 unité », « 12 unités » (« 1 unit », « 12 units ») : un ordre de bataille « racine seule » n'en a qu'une. */
export function unitCount(n: number): string {
  return plural(n, t("unité"), t("unités"));
}

/** Avis de création, de copie ou d'import : « {nom} : 1 unité, modifiable. » */
export function unitsEditable(name: string, n: number): string {
  return t("{name} : {units}, modifiable.", { name, units: unitCount(n) });
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })}`;
}

/** Modèle d'origine à l'écran : en anglais, un ODB du catalogue est nommé par sa formation en désignation OTAN
 *  (« BRIGADE_11BP » → « 11 Para Bde »), jamais par son code interne. */
export function modelLabel(source: string | undefined, label: string): string {
  if (lang() !== "en" || source !== "catalogue") return unitDesignation(label);
  return unitDesignation(label.split("_").pop() ?? label);
}

function OdbCard({ odb, active, onClick }: { odb: OdbSummary; active: boolean; onClick: () => void }) {
  const nation = nationName(odb.nation);
  // Modèle d'origine, rappelé sous le nom quand il en diffère.
  const model = odb.model?.label && bi(odb.model.label) !== odb.name ? modelLabel(odb.model.source, bi(odb.model.label)) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active || undefined}
      className={clsx(
        "flex min-h-8 w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors",
        active ? "bg-selected" : "hover:bg-hover",
      )}
    >
      <UnitSymbol sidc={odb.sidc} size={12} box={44} left={odb.numbered ? odb.number : undefined} right={odb.numbered ? odb.higher : undefined} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ui font-medium text-ink">{unitDesignation(odb.name)}</span>
        <span className="block truncate text-meta text-ink-muted tabular-nums">
          {natureLabel(odb.kind, odb.kind_label, odb.organisation)}
          {nation ? ` · ${nation}` : ""} · {unitCount(odb.units)}
          {odb.user && odb.updated_at ? ` · ${when(odb.updated_at)}` : ""}
          {odb.owner ? ` · ${t("par {nom}", { nom: odb.owner.name })}` : ""}
        </span>
        {model && <span className="block truncate text-meta text-ink-muted">{model}</span>}
      </span>
      {odb.user && itemPrivate(odb) && <Badge className="shrink-0">{t("Privé")}</Badge>}
      {odb.from_exercise && <Badge className="shrink-0">{t("Du dossier")}</Badge>}
      {odb.user && (odb.used_by ?? 0) > 0 && (
        <Badge className="shrink-0" title={t("Dossiers qui en tirent leur force")}>
          {plural(odb.used_by ?? 0, t("dossier"), t("dossiers"))}
        </Badge>
      )}
    </button>
  );
}

export function OdbList({
  side,
  onSide,
  loading,
  user,
  catalog,
  current,
  onPick,
  referenceEnemy = true,
}: {
  side: OdbSide;
  onSide: (side: OdbSide) => void;
  loading: boolean;
  user: OdbSummary[];
  catalog: OdbSummary[];
  current: string | null;
  onPick: (code: string) => void;
  /** Faux : ouvert depuis un dossier qui affronte l'ennemi générique (ennemis des unités françaises cachés). */
  referenceEnemy?: boolean;
}) {
  const enemy = side === "ENI";
  const catalogShown = !enemy || referenceEnemy;
  return (
    <>
      <Segmented
        ariaLabel={t("Camp des ordres de bataille")}
        className="pb-2"
        options={SIDES.map((o) => ({ value: o.value, label: t(o.label), icon: o.icon }))}
        value={side}
        onChange={onSide}
      />
      <SectionLabel className="px-1">{enemy ? t("Vos ordres de bataille ennemis") : t("Vos ordres de bataille")}</SectionLabel>
      {loading ? (
        <p className="flex items-center gap-2 px-1 py-2 text-ui text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Chargement…")}</Waiting>}
        </p>
      ) : user.length === 0 ? (
        <p className="px-1 pb-2 text-ui leading-relaxed text-ink-muted">
          {enemy
            ? referenceEnemy
              ? t("Aucun pour l'instant : partez de la structure ternaire générique ou d'une racine seule, ou dupliquez une division de référence.")
              : t("Aucun pour l'instant : partez de la structure ternaire générique ou d'une racine seule.")
            : t("Aucun pour l'instant : partez de zéro avec les symboles OTAN, copiez un ordre de bataille fourni ou importez celui de l'application Generex pour iPad.")}
        </p>
      ) : (
        user.map((o) => <OdbCard key={o.code} odb={o} active={o.code === current} onClick={() => onPick(o.code)} />)
      )}
      {catalogShown && (
        <>
          <SectionLabel className="px-1">{enemy ? t("Ennemis des unités françaises") : t("Fournis avec Generex (armée de Terre française)")}</SectionLabel>
          {catalog.map((o) => (
            <OdbCard key={o.code} odb={o} active={o.code === current} onClick={() => onPick(o.code)} />
          ))}
        </>
      )}
    </>
  );
}
